<template>
    <component
        :is="tag"
        class="nbutton"
        :class="[`nbutton-${size}`, `nbutton-${type}`, `nbutton-${shape}`, { 'nbutton-disabled': disabled, 'nbutton-loading': loading }]"
        v-bind="tagAttrs"
        @click="handleClick">
        <span
            v-if="loading"
            class="nbutton-loading">
            <i class="loading-icon"></i>
        </span>

        <slot></slot>
    </component>
</template>

<script setup lang="ts">
import { computed, defineEmits } from 'vue';

interface Props {
    tag?: string | object;
    size?: 'small' | 'medium' | 'large';
    type?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'border' | 'transparent' | 'base' | 'linear-light' | 'ghost-base' | 'base-linear';
    disabled?: boolean;
    shape?: 'circle' | 'round';
    loading?: boolean;
    href?: string;
    to?: string | object;
}

const props = withDefaults(defineProps<Props>(), {
    tag: 'button',
    size: 'small',
    type: 'primary',
    disabled: false,
    loading: false,
    shape: 'round'
});

const tagAttrs = computed(() => {
    if (props.tag === 'button') {
        return { type: 'button', disabled: props.disabled || props.loading };
    }
    if (props.tag === 'a') {
        return {
            href: (props.disabled || props.loading) ? undefined : props.href,
            'aria-disabled': (props.disabled || props.loading) ? 'true' : undefined
        };
    }
    if (props.to) {
        return { to: props.to };
    }
    return {};
});

const emit = defineEmits<{
    click: [event: MouseEvent];
}>();

const handleClick = (event: MouseEvent) => {
    if (props.disabled || props.loading) {
        event.preventDefault();
        return;
    }
    emit('click', event);
};
</script>

<style scoped lang="scss">
.nbutton {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    outline: none;
    user-select: none;
    white-space: nowrap;
    line-height: 1;
    box-sizing: border-box;
    font-weight: 700;
    text-decoration: none;

    &:focus {
        outline: none;
    }

    // 尺寸样式
    &-small {
        border-radius: calc(var(--button-small-height) / 2);
        height: var(--button-small-height);
        padding: 0 var(--button-small-padding);
        font-size: var(--button-small-fontsize);

        &.nbutton-circle {
            border-radius: 50%;
            width: var(--button-small-height);
            height: var(--button-small-height);
            padding: 0;
        }
    }

    &-medium {
        border-radius: calc(var(--button-medium-height) / 2);
        height: var(--button-medium-height);
        padding: 0 var(--button-medium-padding);
        font-size: var(--button-medium-fontsize);

        &.nbutton-circle {
            border-radius: 50%;
            width: var(--button-medium-height);
            height: var(--button-medium-height);
            padding: 0;
        }
    }

    &-large {
        border-radius: calc(var(--button-large-height) / 2);
        height: var(--button-large-height);
        padding: 0 var(--button-large-padding);
        font-size: var(--button-large-fontsize);

        &.nbutton-circle {
            border-radius: 50%;
            width: var(--button-large-height);
            height: var(--button-large-height);
            padding: 0;
        }
    }

    // 类型样式
    &-primary {
        background: var(--button-bg);
        color: var(--button-color);

        &:hover:not(.nbutton-disabled):not(.nbutton-loading) {
            opacity: 0.8;
        }
    }

    // 类型样式
    &-base {
        background: var(--color-base);
        color: #fff;

        &:hover:not(.nbutton-disabled):not(.nbutton-loading) {
            opacity: 0.8;
        }
    }

    &-base-linear {
        background: linear-gradient(93deg, #8c55f2 5.27%, #c294ff 93.54%);
        color: #fff;

        &:hover:not(.nbutton-disabled):not(.nbutton-loading) {
            opacity: 0.8;
        }
    }

    &-secondary {
        background: var(--button-secondary-bg);
        color: var(--button-secondary-color);

        &:hover:not(.nbutton-disabled):not(.nbutton-loading) {
            opacity: 0.8;
        }
    }

    &-border {
        background-color: transparent;
        color: var(--button-border-color);
        border: 1px solid var(--button-border-border-color);

        &:hover:not(.nbutton-disabled):not(.nbutton-loading) {
            opacity: 0.8;
        }
    }

    &-border-base {
        background-color: transparent;
        border: 2px solid #f2eaff;
        box-sizing: content-box;

        &:hover:not(.nbutton-disabled):not(.nbutton-loading) {
            border: 2px solid #e6d8fc;
            opacity: 0.8;
        }
    }

    &-border-dark-base {
        background-color: transparent;
        border: 2px solid var(--color-base);

        &:hover:not(.nbutton-disabled):not(.nbutton-loading) {
            opacity: 0.8;
        }
    }

    // &--danger {
    //     background: #f56565;
    //     color: white;

    //     &:hover:not(.nbutton--disabled):not(.nbutton--loading) {
    //         background: #e53e3e;
    //         transform: translateY(-1px);
    //         box-shadow: 0 4px 12px rgba(245, 101, 101, 0.4);
    //     }

    //     &:active:not(.nbutton--disabled):not(.nbutton--loading) {
    //         transform: translateY(0);
    //         background: #c53030;
    //     }
    // }

    &-ghost {
        background: transparent;
        padding: 0;
        color: var(--button-ghost-color);

        &:hover:not(.nbutton-disabled):not(.nbutton-loading) {
            opacity: 0.8;
        }
    }

    &-ghost-base {
        background: transparent;
        padding: 0;
        color: var(--color-base);

        &:hover:not(.nbutton-disabled):not(.nbutton-loading) {
            opacity: 0.8;
        }
    }

    &-transparent {
        background: transparent;
        padding: 0;
        color: var(--button-transparent-color);
        border: 1px solid var(--button-transparent-border-color);

        &:hover:not(.nbutton-disabled):not(.nbutton-loading) {
            opacity: 0.8;
        }
    }

    // 禁用状态
    &-disabled {
        &:not(.nbutton-ghost):not(.nbutton-transparent) {
            background: var(--button-disabled-bg);
            color: var(--button-disabled-color);
            cursor: not-allowed;
            transform: none !important;
            box-shadow: none !important;
        }

        &.nbutton-ghost,
        &.nbutton-transparent {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    // 加载状态
    &-loading {
        background: var(--button-disabled-bg);
        color: var(--button-disabled-color);
        cursor: not-allowed;
    }
}

// // 加载动画
// .loading-icon {
//     width: 14px;
//     height: 14px;
//     border: 2px solid currentColor;
//     border-top-color: transparent;
//     border-radius: 50%;
//     animation: spin 1s linear infinite;
// }

// @keyframes spin {
//     to {
//         transform: rotate(360deg);
//     }
// }

// // 响应式调整
// @media (max-width: 768px) {
//     .nbutton {
//         &--large {
//             height: 40px;
//             padding: 0 20px;
//             font-size: 15px;
//         }

//         &--medium {
//             height: 34px;
//             padding: 0 14px;
//             font-size: 13px;
//         }

//         &--small {
//             height: 26px;
//             padding: 0 10px;
//             font-size: 11px;
//         }
//     }
// }
</style>
